import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { TrendingUp, TrendingDown } from 'lucide-react';

/**
 * Reusable Stats Card component for dashboards
 * Shows a metric with optional trend indicator
 * 
 * @param {string} title - Metric title
 * @param {string|number} value - Main value to display
 * @param {string} prefix - Prefix for value (e.g., "$")
 * @param {string} suffix - Suffix for value (e.g., "%")
 * @param {React.Component} icon - Icon component to display
 * @param {string} iconBgColor - Background color class for icon
 * @param {string} valueColor - Text color class for value
 * @param {number} trend - Percentage change (positive or negative)
 * @param {string} trendLabel - Label for trend (e.g., "vs last month")
 * @param {string} description - Additional description text
 * @param {Function} onClick - Optional click handler for interactive cards
 */
export default function StatsCard({
  title,
  value,
  prefix = '',
  suffix = '',
  icon: Icon,
  iconBgColor = 'bg-blue-500/20',
  iconColor = 'text-blue-400',
  valueColor = 'text-white',
  trend,
  trendLabel = 'vs last period',
  description,
  onClick,
  className = '',
  testId
}) {
  const hasTrend = trend !== undefined && trend !== null;
  const isPositiveTrend = trend > 0;
  
  return (
    <Card 
      className={`bg-slate-900 border-slate-800 ${onClick ? 'cursor-pointer hover:border-slate-700 transition-colors' : ''} ${className}`}
      onClick={onClick}
      data-testid={testId}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-xs text-slate-400 mb-1">{title}</p>
            <p className={`text-2xl font-bold ${valueColor}`}>
              {prefix}{typeof value === 'number' ? value.toLocaleString() : value}{suffix}
            </p>
            
            {hasTrend && (
              <div className="flex items-center gap-1 mt-2">
                {isPositiveTrend ? (
                  <TrendingUp className="h-3 w-3 text-green-400" />
                ) : (
                  <TrendingDown className="h-3 w-3 text-red-400" />
                )}
                <span className={`text-xs ${isPositiveTrend ? 'text-green-400' : 'text-red-400'}`}>
                  {isPositiveTrend ? '+' : ''}{trend}%
                </span>
                <span className="text-xs text-slate-500">{trendLabel}</span>
              </div>
            )}
            
            {description && (
              <p className="text-xs text-slate-500 mt-2">{description}</p>
            )}
          </div>
          
          {Icon && (
            <div className={`p-2 ${iconBgColor} rounded-lg shrink-0`}>
              <Icon className={`h-5 w-5 ${iconColor}`} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
